import { Capacitor } from '@capacitor/core';
import { Toast } from '@capacitor/toast';

interface HealthPlugin {
  requestHealthPermissions(options: { permissions: ('READ_STEPS' | 'READ_WORKOUTS' | 'READ_ACTIVE_CALORIES' | 'READ_TOTAL_CALORIES' | 'READ_DISTANCE' | 'READ_HEART_RATE' | 'READ_ROUTE' | 'READ_MINDFULNESS' | 'READ_SLEEP')[] }): Promise<{ permissions: { [key: string]: boolean }[] }>;
  queryAggregated(options: {
    dataType: 'steps' | 'active-calories' | 'mindfulness';
    startDate: string;
    endDate: string;
    bucket: string;
  }): Promise<{ aggregatedData: Array<{ startDate: string; endDate: string; value: number }> }>;
}

interface HealthAnchoredQueryPlugin {
  startAnchoredQuery(options: { dataType: 'steps' | 'active-calories' | 'sleep' | 'heartRate' | 'oxygenSaturation' | 'atrialFibrillationBurden' }): Promise<{ queryId: string }>;
  stopAnchoredQuery(options: { queryId: string }): Promise<{ success: boolean }>;
  requestAllPermissions(options: { dataTypes: string[] }): Promise<{ success: boolean }>;
  addListener(eventName: 'healthDataUpdate', listenerFunc: (data: {
    queryId: string;
    dataType: string;
    total: number;
    samples: Array<{
      value: number;
      startDate: number;
      endDate: number;
      sourceName: string;
      uuid: string;
      categoryValue?: number;
    }>;
    timestamp: number;
  }) => void): Promise<{ remove: () => Promise<void> }>;
}

export interface HealthDataUpdate {
  type: 'steps' | 'sleep' | 'heartRate' | 'oxygenSaturation' | 'atrialFibrillationBurden';
  value: number | string;
  unit?: string;
  timestamp: string;
  source?: string;
}

export class AppleHealthService {
  private isAvailable = false;
  private hasPermissions = false;
  private healthPlugin: HealthPlugin | null = null;
  private anchoredQueryPlugin: HealthAnchoredQueryPlugin | null = null;
  private updateCallbacks: ((update: HealthDataUpdate) => void)[] = [];
  private activeQueryIds: Map<string, string> = new Map(); // Map dataType -> queryId
  private queryListeners: Map<string, { remove: () => Promise<void> }> = new Map(); // Map dataType -> listener
  private lastStepCount: number = 0;
  private lastSleepData: string | null = null;
  private lastHeartRate: number | null = null;
  private lastOxygenSaturation: number | null = null;
  private lastAtrialFibrillationBurden: number | null = null;

  async initialize(): Promise<boolean> {
    try {
      console.log('🏥 We are testing health right now');
      if (!this.isIOSDevice()) return false;
      if (typeof Capacitor !== 'undefined' && (Capacitor as any).Plugins) {
        const plugins = (Capacitor as any).Plugins as any;
        
        // Log all available plugins for debugging
        const pluginNames = Object.keys(plugins);
        console.log('[Apple Health] Available Capacitor plugins:', pluginNames);
        
        if (plugins.HealthPlugin) {
          this.isAvailable = true;
          this.healthPlugin = plugins.HealthPlugin as HealthPlugin;
          console.log('✅ Apple Health plugin initialized');
        } else {
          console.warn('[Apple Health] HealthPlugin not found in Capacitor plugins');
        }
        
             // Check for the separate HealthAnchoredQueryPlugin
             if (plugins.HealthAnchoredQueryPlugin) {
               this.anchoredQueryPlugin = plugins.HealthAnchoredQueryPlugin as HealthAnchoredQueryPlugin;
               console.log('✅ HealthAnchoredQueryPlugin found and initialized');
               console.log('[Apple Health] Plugin object:', this.anchoredQueryPlugin);
               console.log('[Apple Health] Plugin methods:', Object.keys(this.anchoredQueryPlugin || {}));
               console.log('[Apple Health] addListener exists:', typeof (this.anchoredQueryPlugin as any)?.addListener);
               
               // Test if we can access addListener directly
               const pluginAny = this.anchoredQueryPlugin as any;
               if (pluginAny && typeof pluginAny.addListener === 'function') {
                 console.log('[Apple Health] ✅ addListener method is available');
               } else {
                 console.error('[Apple Health] ❌ addListener method NOT available on plugin');
               }
             } else {
               console.warn('[Apple Health] HealthAnchoredQueryPlugin not found. Make sure it\'s compiled and the app is rebuilt.');
             }
        
        return this.isAvailable;
      }
      return false;
    } catch (error) {
      console.error('Error initializing Apple Health:', error);
      return false;
    }
  }

  getIsAvailable(): boolean { return this.isAvailable; }
  getHasPermissions(): boolean { return this.hasPermissions; }

  async requestPermissions(): Promise<boolean> {
    if (!this.isAvailable || !this.healthPlugin) {
      console.error('[Apple Health] Service not available or plugin not initialized');
      throw new Error('Apple Health not available');
    }
    
    try {
      // Use the plugin's HealthPermission format, NOT HealthKit identifiers
      // The plugin expects: 'READ_STEPS', 'READ_WORKOUTS', etc.
      const pluginPermissions: ('READ_STEPS' | 'READ_WORKOUTS' | 'READ_ACTIVE_CALORIES' | 'READ_TOTAL_CALORIES' | 'READ_DISTANCE' | 'READ_HEART_RATE' | 'READ_ROUTE' | 'READ_MINDFULNESS' | 'READ_SLEEP')[] = [
        'READ_STEPS',
        'READ_HEART_RATE',
        'READ_SLEEP',
        // Note: Oxygen saturation may need to be requested separately or may be included with heart rate
      ];
      
      console.log('[Apple Health] Requesting permissions for:', pluginPermissions);
      console.log('[Apple Health] Plugin available:', !!this.healthPlugin);
      console.log('[Apple Health] Plugin method exists:', typeof this.healthPlugin.requestHealthPermissions === 'function');
      
      // Ensure we have a valid plugin reference
      if (!this.healthPlugin || typeof this.healthPlugin.requestHealthPermissions !== 'function') {
        console.error('[Apple Health] Plugin method not available');
        return false;
      }
      
      // Add a small delay to ensure UI is ready
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Try calling with proper error handling
      console.log('[Apple Health] Calling requestHealthPermissions...');
      
      const result = await this.healthPlugin.requestHealthPermissions({ 
        permissions: pluginPermissions 
      });
      
      console.log('[Apple Health] Permission request result:', result);
      
      if (result && result.permissions) {
        // The plugin returns permissions as an object: {"READ_STEPS": true}
        // OR as an array of objects: [{"READ_STEPS": true}]
        let granted: string[] = [];
        
        if (Array.isArray(result.permissions)) {
          // Handle array format
          granted = result.permissions
            .filter(perm => {
              const values = Object.values(perm);
              return values.some(v => v === true);
            })
            .flatMap(perm => Object.keys(perm));
        } else if (typeof result.permissions === 'object') {
          // Handle object format: {"READ_STEPS": true}
          granted = Object.keys(result.permissions).filter(
            key => result.permissions[key] === true
          );
        }
        
        console.log('[Apple Health] Granted permissions:', granted);
        this.hasPermissions = granted.length > 0;
        console.log('[Apple Health] Has permissions:', this.hasPermissions);
        return this.hasPermissions;
      }
      this.hasPermissions = false;
      return false;
    } catch (error: any) {
      console.error('[Apple Health] Error requesting permissions:', error);
      console.error('[Apple Health] Error details:', {
        message: error?.message,
        code: error?.code,
        name: error?.name,
        stack: error?.stack
      });
      this.hasPermissions = false;
      // Don't throw - return false instead so app can continue
      return false;
    }
  }

  async startImmediateMonitoring(onUpdate: (update: HealthDataUpdate) => void): Promise<void> {
    console.log('🏥 We are testing health right now - Starting immediate monitoring with HKAnchoredObjectQuery');
    if (!this.isAvailable || !this.healthPlugin) {
      const error = new Error('Apple Health not available');
      console.error('[Apple Health]', error.message);
      throw error;
    }
    if (!this.hasPermissions) {
      const error = new Error('Permissions not granted');
      console.error('[Apple Health]', error.message);
      throw error;
    }
    if (!this.anchoredQueryPlugin) {
      const error = new Error('HealthAnchoredQueryPlugin not available. Make sure HealthAnchoredQueryPlugin.swift is compiled and the app is rebuilt. Run: npx cap sync ios');
      console.error('[Apple Health]', error.message);
      console.error('[Apple Health] Available plugins:', Object.keys((Capacitor as any).Plugins || {}));
      throw error;
    }
    
    this.updateCallbacks.push(onUpdate);
    
    // Data types to monitor
    // Note: atrialFibrillationBurden requires iOS 16.0+
    const dataTypes: Array<'steps' | 'sleep' | 'heartRate' | 'oxygenSaturation' | 'atrialFibrillationBurden'> = [
      'steps',
      'sleep',
      'heartRate',
      'oxygenSaturation',
      'atrialFibrillationBurden' // iOS 16.0+ only
    ];
    
    // Request all permissions at once before starting any queries
    // Always request via anchored query plugin to ensure oxygenSaturation is included
    // Add a delay to ensure any previous permission dialogs are dismissed
    console.log('[Apple Health] Requesting all permissions at once via anchored query plugin...');
    try {
      // Wait a bit in case the main plugin just showed a permission dialog
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.anchoredQueryPlugin.requestAllPermissions({ dataTypes });
      console.log('[Apple Health] All permissions requested');
      // Wait a bit more for the permission dialog to be dismissed before starting queries
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.warn('[Apple Health] Permission request failed, continuing anyway:', error);
    }
    
    console.log('[Apple Health] Starting anchored queries for:', dataTypes);
    
    // Start queries sequentially to ensure each completes before the next starts
    for (const dataType of dataTypes) {
      try {
        const result = await this.anchoredQueryPlugin.startAnchoredQuery({ dataType });
        console.log(`[Apple Health] startAnchoredQuery result for ${dataType}:`, result);
        console.log(`[Apple Health] Result type:`, typeof result);
        console.log(`[Apple Health] Result keys:`, result ? Object.keys(result) : 'null');
        
        // The result might be the queryId directly, or it might be an object with queryId property
        const queryId = (result as any)?.queryId || (typeof result === 'string' ? result : null);
        
        if (queryId) {
          this.activeQueryIds.set(dataType, queryId);
          console.log(`[Apple Health] ✅ Started anchored query for ${dataType}:`, queryId);
          console.log(`[Apple Health] Active query IDs:`, Array.from(this.activeQueryIds.entries()));
        } else {
          console.error(`[Apple Health] ❌ No queryId found for ${dataType}. Result:`, result);
          console.error(`[Apple Health] Result structure:`, JSON.stringify(result));
        }
      } catch (error: any) {
        // Skip atrial fibrillation burden on iOS < 16.0
        if (dataType === 'atrialFibrillationBurden' && error?.message?.includes('iOS 16')) {
          console.warn(`[Apple Health] Skipping ${dataType} - requires iOS 16.0+`);
          continue;
        }
        console.error(`[Apple Health] Failed to start query for ${dataType}:`, error);
        // Continue with next query even if one fails
      }
    }
    
    // Set up a single listener for all data types
    console.log('[Apple Health] Setting up healthDataUpdate listener...');
    console.log('[Apple Health] Plugin reference:', this.anchoredQueryPlugin);
    console.log('[Apple Health] addListener method:', typeof this.anchoredQueryPlugin?.addListener);
    
    // Try accessing the plugin directly from Capacitor
    const CapacitorPlugins = (Capacitor as any).Plugins as any;
    const directPlugin = CapacitorPlugins?.HealthAnchoredQueryPlugin;
    console.log('[Apple Health] Direct plugin access:', directPlugin);
    console.log('[Apple Health] Direct plugin addListener:', typeof directPlugin?.addListener);
    
    try {
      // Try using the direct plugin reference if available
      const pluginToUse = directPlugin || this.anchoredQueryPlugin;
      console.log('[Apple Health] Using plugin:', pluginToUse);
      
      // Ensure we're using the correct plugin reference
      if (!pluginToUse || typeof pluginToUse.addListener !== 'function') {
        throw new Error('Plugin addListener method not available');
      }
      
      console.log('[Apple Health] About to call addListener on plugin...');
      const listener = await pluginToUse.addListener('healthDataUpdate', (data: any) => {
        console.log('[Apple Health] ========================================');
        console.log('[Apple Health] 📥 RECEIVED healthDataUpdate EVENT');
        console.log('[Apple Health] Full data:', JSON.stringify(data, null, 2));
        console.log('[Apple Health] Data type:', typeof data);
        console.log('[Apple Health] Data keys:', data ? Object.keys(data) : 'null');
        console.log('[Apple Health] ========================================');
      
      // Use the dataType directly from the payload (Swift includes it)
      const updateDataType = data.dataType as 'steps' | 'sleep' | 'heartRate' | 'oxygenSaturation' | 'atrialFibrillationBurden' | null;
      
      if (!updateDataType) {
        console.warn(`[Apple Health] ⚠️ Unknown or missing dataType in update: ${data.dataType}`);
        console.warn(`[Apple Health] Available dataType values: steps, sleep, heartRate, oxygenSaturation, atrialFibrillationBurden`);
        return;
      }
      
      console.log(`[Apple Health] ✅ Processing update for dataType: ${updateDataType}, total: ${data.total}`);
      
      // Process update based on data type
      if (updateDataType === 'steps') {
        const totalSteps = Math.round(data.total);
        console.log(`[Apple Health] Processing steps update: totalSteps=${totalSteps}, lastStepCount=${this.lastStepCount}`);
        if (totalSteps !== this.lastStepCount) {
          const difference = totalSteps - this.lastStepCount;
          console.log(`[Apple Health] ✅ Step count changed: ${this.lastStepCount} -> ${totalSteps} (difference: ${difference > 0 ? '+' : ''}${difference} steps)`);
          this.lastStepCount = totalSteps;
          console.log('[Apple Health] Calling notifyUpdate for steps...');
          this.notifyUpdate({
            type: 'steps',
            value: totalSteps,
            unit: 'steps',
            timestamp: new Date(data.timestamp).toISOString(),
            source: 'Apple Health'
          });
        } else {
          console.log(`[Apple Health] ⏭️ Step count unchanged (${totalSteps}), skipping notification`);
        }
      } else if (updateDataType === 'atrialFibrillationBurden') {
        // Atrial fibrillation burden always sends regardless of change
        const burden = Math.round(data.total * 100) / 100; // Round to 2 decimal places
        console.log('[Apple Health] Atrial fibrillation burden received:', burden, '%');
        this.lastAtrialFibrillationBurden = burden;
        this.notifyUpdate({
          type: 'atrialFibrillationBurden',
          value: burden,
          unit: '%',
          timestamp: new Date(data.timestamp).toISOString(),
          source: 'Apple Health'
        });
      } else if (updateDataType === 'sleep') {
        // Sleep always sends regardless of change
        const sleepHours = data.total;
        console.log('[Apple Health] Sleep data received:', sleepHours, 'hours');
        this.lastSleepData = `${sleepHours}`;
        this.notifyUpdate({
          type: 'sleep',
          value: `${sleepHours.toFixed(2)} hours`,
          unit: 'hours',
          timestamp: new Date(data.timestamp).toISOString(),
          source: 'Apple Health'
        });
      } else if (updateDataType === 'heartRate') {
        // Heart rate always sends regardless of change
        const heartRate = Math.round(data.total);
        console.log('[Apple Health] Heart rate received:', heartRate, 'bpm');
        this.lastHeartRate = heartRate;
        this.notifyUpdate({
          type: 'heartRate',
          value: heartRate,
          unit: 'bpm',
          timestamp: new Date(data.timestamp).toISOString(),
          source: 'Apple Health'
        });
      } else if (updateDataType === 'oxygenSaturation') {
        // Oxygen saturation always sends regardless of change
        const oxygenSat = Math.round(data.total * 100) / 100; // Round to 2 decimal places
        console.log('[Apple Health] Oxygen saturation received:', oxygenSat, '%');
        this.lastOxygenSaturation = oxygenSat;
        this.notifyUpdate({
          type: 'oxygenSaturation',
          value: oxygenSat,
          unit: '%',
          timestamp: new Date(data.timestamp).toISOString(),
          source: 'Apple Health'
        });
      }
      });
      
      console.log('[Apple Health] ✅ Listener registered successfully, listener object:', listener);
      this.queryListeners.set('all', listener);
    } catch (error) {
      console.error('[Apple Health] ❌ Failed to register listener:', error);
      throw error;
    }
  }

  async stopImmediateMonitoring(): Promise<void> {
    // Stop all active queries
    if (this.anchoredQueryPlugin) {
      for (const [dataType, queryId] of this.activeQueryIds.entries()) {
        try {
          await this.anchoredQueryPlugin.stopAnchoredQuery({ queryId });
          console.log(`[Apple Health] Stopped anchored query for ${dataType}:`, queryId);
        } catch (error) {
          console.error(`[Apple Health] Error stopping query for ${dataType}:`, error);
        }
      }
      this.activeQueryIds.clear();
    }
    
    // Remove all listeners
    for (const [key, listener] of this.queryListeners.entries()) {
      try {
        await listener.remove();
      } catch (error) {
        console.error(`[Apple Health] Error removing listener ${key}:`, error);
      }
    }
    this.queryListeners.clear();
    
    this.updateCallbacks = [];
    this.lastStepCount = 0;
    this.lastSleepData = null;
    this.lastHeartRate = null;
    this.lastOxygenSaturation = null;
    this.lastAtrialFibrillationBurden = null;
  }

  private async notifyUpdate(update: HealthDataUpdate): Promise<void> {
    let message = '';
    if (update.type === 'steps') {
      message = `New Steps: ${typeof update.value === 'number' ? update.value.toLocaleString() : update.value} ${update.unit || 'steps'}`;
    } else if (update.type === 'atrialFibrillationBurden') {
      message = `AFib Burden: ${update.value}${update.unit || '%'}`;
    } else if (update.type === 'sleep') {
      message = `Sleep: ${update.value}`;
    } else if (update.type === 'heartRate') {
      message = `Heart Rate: ${update.value} ${update.unit || 'bpm'}`;
    } else if (update.type === 'oxygenSaturation') {
      message = `Oxygen: ${update.value}${update.unit || '%'}`;
    }
    
    // Log the update
    console.log('[Apple Health] 📊 New data received:', update);
    console.log('[Apple Health] 📞 Notifying', this.updateCallbacks.length, 'callback(s)...');
    
    try {
      if (Capacitor.isNativePlatform()) {
        await Toast.show({
          text: message,
          duration: 'long',
          position: 'top'
        });
      } else {
        alert(message);
      }
    } catch (error) {
      console.error('[Apple Health] Error showing alert:', error);
    }
    
    // Notify callbacks
    this.updateCallbacks.forEach((callback, index) => {
      try {
        console.log(`[Apple Health] 🔔 Calling callback ${index + 1}/${this.updateCallbacks.length}...`);
        callback(update);
        console.log(`[Apple Health] ✅ Callback ${index + 1} completed`);
      } catch (error) {
        console.error(`[Apple Health] ❌ Error in update callback ${index + 1}:`, error);
      }
    });
    console.log('[Apple Health] ✅ All callbacks notified');
  }

  private isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }
}
